import { useState } from "react";
import { useInput, useTranslate } from "ra-core";
import { Trash2, Plus, RefreshCw, Infinity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormField, FormLabel } from "@/components/admin/form";
import { InputHelperText } from "@/components/admin/input-helper-text";

type Entry = { date: string; amount: string };

type RecurringConfig = {
  _type: "recurring";
  period: string;
  amount: string;
  start_date: string;
  unlimited: true;
};

const isRecurring = (value: unknown): value is RecurringConfig =>
  !!value && typeof value === "object" && (value as RecurringConfig)._type === "recurring";

const parseEntries = (value: unknown): Entry[] => {
  if (!value || isRecurring(value)) return [];
  const obj = value as Record<string, unknown>;
  if (Object.keys(obj).length === 0) return [];
  return Object.entries(obj)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, amount]) => ({ date, amount: String(amount) }));
};

const toObject = (entries: Entry[]): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const { date, amount } of entries) {
    if (date) result[date] = parseFloat(amount) || 0;
  }
  return result;
};

const addMonths = (dateStr: string, months: number): string => {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const PERIODS: { value: string; label: string; months: number }[] = [
  { value: "monthly", label: "Ежемесячно", months: 1 },
  { value: "quarterly", label: "Ежеквартально", months: 3 },
  { value: "biannual", label: "Каждые полгода", months: 6 },
  { value: "annual", label: "Ежегодно", months: 12 },
];

const PERIOD_LABELS: Record<string, string> = {
  monthly: "Ежемесячно",
  quarterly: "Ежеквартально",
  biannual: "Каждые полгода",
  annual: "Ежегодно",
};

export const PaymentScheduleInput = ({ source }: { source: string }) => {
  const translate = useTranslate();
  const { field } = useInput({ source });

  const [entries, setEntries] = useState<Entry[]>(() => parseEntries(field.value));

  const savedRecurring = isRecurring(field.value) ? field.value : null;
  const [startDate, setStartDate] = useState(savedRecurring?.start_date ?? "");
  const [period, setPeriod] = useState(savedRecurring?.period ?? "monthly");
  const [count, setCount] = useState("12");
  const [amount, setAmount] = useState(savedRecurring?.amount ?? "");
  const [unlimited, setUnlimited] = useState(!!savedRecurring);

  const updateEntries = (next: Entry[]) => {
    setEntries(next);
    field.onChange(toObject(next));
  };

  const addRow = () => updateEntries([...entries, { date: "", amount: "" }]);
  const removeRow = (i: number) => updateEntries(entries.filter((_, idx) => idx !== i));
  const changeRow = (i: number, key: keyof Entry, value: string) =>
    updateEntries(entries.map((e, idx) => (idx === i ? { ...e, [key]: value } : e)));

  const generate = () => {
    if (!startDate || !amount) return;
    if (unlimited) {
      field.onChange({
        _type: "recurring",
        period,
        amount,
        start_date: startDate,
        unlimited: true,
      } as RecurringConfig);
      setEntries([]);
    } else {
      const months = PERIODS.find((p) => p.value === period)?.months ?? 1;
      const n = Math.max(1, parseInt(count) || 1);
      const generated: Entry[] = Array.from({ length: n }, (_, i) => ({
        date: addMonths(startDate, i * months),
        amount,
      }));
      updateEntries(generated);
    }
  };

  const label = translate("resources.contracts.fields.payment_schedule", {
    _: "График платежей",
  });

  return (
    <FormField name={source}>
      <FormLabel>{label}</FormLabel>
      <Tabs defaultValue="manual" className="mt-1">
        <TabsList className="w-full">
          <TabsTrigger value="manual" className="flex-1">Разовые / вручную</TabsTrigger>
          <TabsTrigger value="recurring" className="flex-1">Регулярные</TabsTrigger>
        </TabsList>

        {/* Manual tab */}
        <TabsContent value="manual" className="flex flex-col gap-2 pt-2">
          {entries.length > 0 && (
            <div className="grid grid-cols-[1fr_1fr_2rem] gap-2 text-xs text-muted-foreground px-1">
              <span>Дата</span><span>Сумма</span><span />
            </div>
          )}
          {entries.map((entry, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_2rem] gap-2 items-center">
              <Input type="date" value={entry.date} onChange={(e) => changeRow(i, "date", e.target.value)} />
              <Input type="number" value={entry.amount} onChange={(e) => changeRow(i, "amount", e.target.value)} placeholder="Сумма" />
              <Button variant="ghost" size="icon" type="button" onClick={() => removeRow(i)} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" type="button" onClick={addRow} className="w-full mt-1">
            <Plus className="h-4 w-4 mr-2" />Добавить строку
          </Button>
        </TabsContent>

        {/* Recurring tab */}
        <TabsContent value="recurring" className="flex flex-col gap-4 pt-2">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Дата первого платежа</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Периодичность</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Сумма каждого платежа</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Сумма" />
            </div>
            <div className="flex flex-col gap-1 justify-end">
              <label className="flex items-center gap-2 cursor-pointer pb-1">
                <Checkbox
                  checked={unlimited}
                  onCheckedChange={(v) => setUnlimited(!!v)}
                />
                <span className="text-sm flex items-center gap-1">
                  Бессрочно <Infinity className="h-4 w-4 text-muted-foreground" />
                </span>
              </label>
              {!unlimited && (
                <Input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} placeholder="Количество" />
              )}
            </div>
          </div>

          <Button
            variant="default"
            type="button"
            onClick={generate}
            disabled={!startDate || !amount || (!unlimited && !count)}
            className="w-full"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {unlimited ? "Сохранить бессрочный график" : "Сформировать график"}
          </Button>

          {/* Preview */}
          {unlimited && isRecurring(field.value) && (
            <div className="rounded-md border px-4 py-3 text-sm flex items-center gap-2">
              <Infinity className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>
                {PERIOD_LABELS[field.value.period] ?? field.value.period},{" "}
                {field.value.amount} ₽, начиная с {field.value.start_date} — бессрочно
              </span>
            </div>
          )}
          {!unlimited && entries.length > 0 && (
            <div className="rounded-md border">
              <div className="grid grid-cols-2 gap-2 px-3 py-2 text-xs text-muted-foreground border-b">
                <span>Дата</span><span>Сумма</span>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {entries.map((e, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 px-3 py-1.5 text-sm border-b last:border-0">
                    <span>{e.date}</span><span>{e.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <InputHelperText helperText="Реальные оплаты ведутся в платежах." />
    </FormField>
  );
};
